import React, { useMemo } from "react";
import PropTypes from "prop-types";
import Card from "components/Card";
import { Select, TagsInput } from "components/Form";
import { GET_ORGANIZATIONS } from "graphql/queries";
import { SORT } from "constants/index";
import { Grid } from "components/Containers";
import { useAuth, useMissionRoles, useMissions } from "hooks";
import { ApplicantListMissionRoleFilter } from "components/Missions/Applicants";

/**
 * Filters
 * @param {Object} selected
 * @param {Function} setSelected
 * @param {Boolean} hideOrganizationFilter
 * @param {Boolean} hideMissionFilter
 * @param {Boolean} hideRoleFilter
 * @param {Boolean} showOnlyPublishedMissions
 * @param {Boolean} showOnlyPublishedRoles
 */
const ApplicantListFilters = ({
  selected,
  setSelected,
  hideOrganizationFilter,
  hideMissionFilter,
  hideRoleFilter,
  showOnlyPublishedMissions,
  showOnlyPublishedRoles,
}) => {
  const { isAdmin, isClient, isShowcase } = useAuth();

  const { data: missionRoles, loading: loadingRoles } = useMissionRoles({
    missionId: selected?.missionId,
    skipQuery: !selected?.missionId && !isClient,
    filters: { published: showOnlyPublishedRoles },
  });

  const { data: missions, loading: loadingMissions } = useMissions({
    filters: {
      organizationId: selected?.organizationId,
      ...(isShowcase ? { demo: true } : {}),
      ...(showOnlyPublishedMissions ? { published: true } : {}), // null would return all missions
    },
    skip: !selected?.organizationId,
  });

  const missionOptions = useMemo(() => (missions || []).map(({ name, id }) => ({ name, id })), [missions]);

  const roleOptions = useMemo(
    () =>
      (missionRoles || []).map(({ name, id, mission }) => ({
        name,
        id,
        missionId: mission?.id,
        organizationId: mission?.organization?.id,
      })),
    [missionRoles]
  );

  return (
    <Card>
      <Grid cols={3}>
        {!hideOrganizationFilter && (
          <Grid.col>
            <Select
              value={selected.organizationId}
              queryKeyName={"organizations"}
              queryName={GET_ORGANIZATIONS}
              placeholder="Organization"
              labelKey="name"
              queryVariables={{
                first: 50,
                orderBy: {
                  id: SORT.desc,
                },
              }}
              valueKey="id"
              onChange={(_, val) => {
                setSelected((prev) => ({
                  ...prev,
                  missionId: null,
                  missionRoleIds: [],
                  organizationId: val,
                }));
              }}
              querySearchFieldName="search"
              noOptionsMessage="No organization found"
              isSearchable
              isClearable
            />
          </Grid.col>
        )}
        {!hideMissionFilter && (
          <Grid.col>
            <Select
              options={missionOptions}
              loading={loadingMissions}
              disabled={!selected.organizationId}
              value={selected.missionId}
              placeholder={!selected.organizationId ? "Select organization first" : "Mission"}
              labelKey="name"
              valueKey="id"
              onChange={(_, val) => {
                setSelected((prev) => ({
                  ...prev,
                  missionId: val,
                  ...(val && { missionRoleIds: [] }),
                }));
              }}
              noOptionsMessage="No mission found"
              isSearchable
              isClearable
            />
          </Grid.col>
        )}
        {!hideRoleFilter && (
          <Grid.col>
            {isAdmin ? (
              <ApplicantListMissionRoleFilter
                selected={selected}
                setSelected={setSelected}
                showOnlyPublishedRoles={showOnlyPublishedRoles}
              />
            ) : (
              <TagsInput
                options={roleOptions}
                disabled={!selected.missionId && !isClient}
                loading={loadingRoles}
                value={selected.missionRoleIds}
                placeholder={!selected.missionId && !isClient ? "Select mission first" : "Role"}
                labelKey="name"
                valueKey="id"
                onChange={(_, val) => setSelected((prev) => ({ ...prev, missionRoleIds: val }))}
                isClearable={!isClient}
                minLength={0}
              />
            )}
          </Grid.col>
        )}
      </Grid>
    </Card>
  );
};

ApplicantListFilters.propTypes = {
  selected: PropTypes.object,
  setSelected: PropTypes.func,
  hideOrganizationFilter: PropTypes.bool,
  hideMissionFilter: PropTypes.bool,
  hideRoleFilter: PropTypes.bool,
  showOnlyPublishedMissions: PropTypes.bool,
  showOnlyPublishedRoles: PropTypes.bool,
};

export default ApplicantListFilters;
